import {useCustomCopilotsEnabled} from '@github-ui/copilot-spaces/hooks'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {Icon} from '@github-ui/pacer/Icon'
import {Tooltip} from '@github-ui/pacer/Tooltip'
import {Link, type LinkProps} from '@github-ui/react-router'
import {ArrowUpRightIcon, CommentDiscussionIcon, ComposeIcon, SpaceIcon} from '@primer/octicons-react'
import {Label} from '@primer/react'
import {clsx} from 'clsx'
import {type MouseEvent, type ReactNode, useCallback, useMemo} from 'react'

import {useActivePlugin} from '../../hooks/use-active-plugin'
import {useNavigateToNewThread} from '../../hooks/use-navigate-to-new-thread'
import {usePlugins} from '../../plugin/ImmersivePluginsProvider'
import {COPILOT_PATH, COPILOT_SPACES_PATH} from '../../utils/copilot-chat-helpers'
// eslint-disable-next-line no-restricted-imports
import {useChatState} from '../../utils/CopilotChatContext'
import {useIsCopilotSpacesPage} from '../../utils/custom-copilots-helpers'
import styles from './ServiceList.module.css'

type ServiceListItemProps = {
  children?: ReactNode
  href: string
  icon: React.ElementType
  onLinkClick?: (e: MouseEvent<HTMLAnchorElement>, page: string) => void
  id: string
  displayName: string
  condensed?: boolean
  label?: ReactNode
  external?: boolean
  /**
   * A stringified JSON object that will be merged into the existing analytics data
   */
  'data-analytics-event'?: string
} & Pick<LinkProps, 'aria-current'>

function ServiceListItem({
  'aria-current': ariaCurrent,
  'data-analytics-event': dataAnalyticsEvent,
  href,
  icon,
  onLinkClick,
  id,
  displayName,
  condensed,
  label,
  external,
}: ServiceListItemProps) {
  const handleLinkClick = (e: MouseEvent<HTMLAnchorElement>) => {
    onLinkClick?.(e, id)
  }

  const commonLinkProps = {
    'aria-current': ariaCurrent,
    'data-analytics-event': dataAnalyticsEvent,
    className: styles.link,
    onClick: handleLinkClick,
  }

  const linkContent = external ? (
    <a href={href} className={styles.link} aria-label={displayName} rel="noopener noreferrer">
      <Icon size={16} icon={icon} />
      <div className={clsx(styles.content, condensed && styles.condensed)}>
        <div className={styles.textWrapper}>
          <div className={styles.text}>{displayName}</div>
        </div>
        <div className={styles.label}>
          <div className={styles.externalLabel}>
            <span className={styles.previewLabel}>{label}</span>
            <ArrowUpRightIcon className={styles.externalIcon} />
          </div>
        </div>
      </div>
    </a>
  ) : (
    <Link to={href} {...commonLinkProps} aria-label={displayName}>
      <Icon size={16} icon={icon} />
      <div className={clsx(styles.content, condensed && styles.condensed)}>
        <div className={styles.textWrapper}>
          <div className={styles.text}>{displayName}</div>
        </div>
        <div className={styles.label}>{label}</div>
      </div>
    </Link>
  )

  return (
    <li className={styles.section}>
      <div className={styles.item} data-current={ariaCurrent === 'page' ? 'true' : undefined}>
        {/* Avoid remounting which breaks CSS transitions */}
        <Tooltip text={condensed ? displayName : ''}>{linkContent}</Tooltip>
      </div>
    </li>
  )
}

interface ServiceListProps {
  toggleFloatingSidebar: () => void
  isSidebarCondensed: boolean
}

interface SortableItem {
  type: 'spaces' | 'plugin'
  component: ReactNode
  displayName: string
}

const serviceListAnalyticsData = JSON.stringify({source: 'ServiceList'})

export function ServiceList({toggleFloatingSidebar, isSidebarCondensed}: ServiceListProps) {
  const chatState = useChatState()
  const {selectedThreadID} = chatState
  const activePluginId = useActivePlugin()?.id
  const plugins = usePlugins()
  const navigateToNewThread = useNavigateToNewThread()
  const isCustomCopilotsEnabled = useCustomCopilotsEnabled()

  const isSpacesPage = useIsCopilotSpacesPage()
  const isMainPage = !selectedThreadID && !activePluginId && !isSpacesPage

  const handleNewThread = async (e: MouseEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.metaKey || e.ctrlKey) return

    toggleFloatingSidebar()
    await navigateToNewThread({clearTopic: true, includeThreads: false})

    sendEvent('dotcom_chat.activate', {target: 'SIDEBAR_CONVERSATION_NEW', mode: 'immersive'})
  }

  const handleSpacesNavigate = useCallback(
    (e: MouseEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.metaKey || e.ctrlKey) return

      toggleFloatingSidebar()
    },
    [toggleFloatingSidebar],
  )

  const handlePluginNavigate = useCallback(
    (e: MouseEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.metaKey || e.ctrlKey) return

      toggleFloatingSidebar()
    },
    [toggleFloatingSidebar],
  )

  const showSpacesPreviewLabel = !isFeatureEnabled('copilot_spaces_ga')

  const sortedItems = useMemo(() => {
    const spacesItem: SortableItem[] = isCustomCopilotsEnabled
      ? [
          {
            type: 'spaces',
            component: (
              <ServiceListItem
                key="spaces"
                aria-current={isSpacesPage ? 'page' : undefined}
                data-analytics-event={serviceListAnalyticsData}
                displayName="Spaces"
                href={COPILOT_SPACES_PATH}
                icon={SpaceIcon}
                condensed={isSidebarCondensed}
                id="spaces"
                onLinkClick={handleSpacesNavigate}
                label={showSpacesPreviewLabel && <Label variant="success">Preview</Label>}
              />
            ),
            displayName: 'Spaces',
          },
        ]
      : []

    const pluginItems = plugins.flatMap(p => {
      if (p.navigationItems && p.navigationItems.length > 0) {
        return p.navigationItems.map(item => ({
          type: 'plugin' as const,
          component: (
            <ServiceListItem
              key={`${p.id}-${item.id}`}
              aria-current={activePluginId === p.id ? 'page' : undefined}
              data-analytics-event={serviceListAnalyticsData}
              displayName={item.displayName}
              href={item.href}
              icon={item.icon}
              condensed={isSidebarCondensed}
              id={item.id}
              onLinkClick={item.external ? undefined : handlePluginNavigate}
              label={item.label}
              external={item.external}
            />
          ),
          displayName: item.displayName,
        }))
      }

      return []
    })

    const defaultToAgent = isFeatureEnabled('copilot_default_to_task_input')
    return [...spacesItem, ...pluginItems].sort((a, b) => {
      // If feature flag is enabled, prioritize 'Agents' first
      if (defaultToAgent) {
        if (a.displayName === 'Agents') return -1
        if (b.displayName === 'Agents') return 1
      }
      return a.displayName.localeCompare(b.displayName)
    })
  }, [
    isCustomCopilotsEnabled,
    isSpacesPage,
    isSidebarCondensed,
    handleSpacesNavigate,
    showSpacesPreviewLabel,
    plugins,
    activePluginId,
    handlePluginNavigate,
  ])

  const defaultToAgent = isFeatureEnabled('copilot_default_to_task_input')
  const items = sortedItems.map(item => item.component)
  const chatItem = (
    <ServiceListItem
      key="home"
      aria-current={isMainPage ? 'page' : undefined}
      data-analytics-event={serviceListAnalyticsData}
      displayName={defaultToAgent ? 'Chat' : 'New chat'}
      href={COPILOT_PATH}
      icon={defaultToAgent ? CommentDiscussionIcon : ComposeIcon}
      condensed={isSidebarCondensed}
      id="home"
      onLinkClick={handleNewThread}
    />
  )

  const showAgentsFirst = isFeatureEnabled('copilot_default_to_task_input') && sortedItems[0]?.displayName === 'Agents'
  const allItems = showAgentsFirst && items[0] ? [items[0], chatItem, ...items.slice(1)] : [chatItem, ...items]

  return <ul className={styles.list}>{allItems}</ul>
}
